/**
 * 跟场评分表（motion.coachRubric.dimensions）。
 *
 * 按参与者逐维度打 1~5 分，保存在模块级内存 store（不要求后端持久化）；
 * 走到复盘台或导出战报时仍会带上（战报 options.scores）。
 */
import { type MotionPackage } from '@zongheng/core-schema';
import { useArena } from '../../state/ArenaContext.tsx';
import { Badge, EmptyState, cx } from '../ui/primitives.tsx';
import { setCoachScore, useCoachScores } from './scoreStore.ts';

const SCORE_VALUES = [1, 2, 3, 4, 5] as const;

function weightedTotal(
  rubric: MotionPackage['coachRubric'],
  entry: Record<string, number>,
): number | null {
  let weighted = 0;
  let weightSum = 0;
  for (const dimension of rubric.dimensions) {
    const score = entry[dimension.id];
    if (typeof score !== 'number' || !Number.isFinite(score)) continue;
    weighted += score * dimension.weight;
    weightSum += dimension.weight;
  }
  return weightSum > 0 ? weighted / weightSum : null;
}

export function ScoreTable({ motion }: { motion: MotionPackage }) {
  const { state } = useArena();
  const scores = useCoachScores();
  const rubric = motion.coachRubric;
  const debaters = state.participants.filter((participant) => participant.role === 'DEBATER');

  return (
    <section
      className="rounded-xl border border-white/10 bg-bg-surface/80 backdrop-blur"
      data-testid="coach-score-table"
    >
      <header className="flex flex-wrap items-start justify-between gap-2 border-b border-white/5 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold tracking-wide text-slate-100">跟场评分</h2>
          <p className="mt-0.5 text-xs text-slate-400">
            {rubric.dimensions.length} 个维度 · 1~5 分 · 仅本机内存保存，导出战报时一并渲染
          </p>
        </div>
        <div className="flex flex-wrap gap-1">
          {rubric.dimensions.map((dimension) => (
            <Badge key={dimension.id} tone="neutral">
              {dimension.label} ×{dimension.weight}
            </Badge>
          ))}
        </div>
      </header>
      <div className="grid gap-3 p-3 lg:grid-cols-2">
        {debaters.length === 0 ? (
          <div className="lg:col-span-2">
            <EmptyState title="暂无辩手可评分" detail="辩手加入房间后显示评分表。" />
          </div>
        ) : (
          debaters.map((participant) => {
            const entry = scores[participant.id] ?? {};
            const total = weightedTotal(rubric, entry);
            return (
              <div
                key={participant.id}
                className={cx(
                  'rounded-lg border px-3 py-2',
                  participant.side === 'AFF'
                    ? 'border-team-aff/25 bg-team-aff/[0.05]'
                    : 'border-team-neg/25 bg-team-neg/[0.05]',
                )}
                data-testid={`score-row-${participant.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs font-medium text-slate-100">
                    {participant.name}
                    <span className="ml-1 text-[10px] text-slate-500">
                      {participant.side === 'AFF' ? '正方' : '反方'}
                      {participant.seat ? ` · ${participant.seat} 辩` : ''}
                    </span>
                  </div>
                  <span className="mono text-[11px] text-slate-400">
                    加权 {total === null ? '—' : total.toFixed(2)}
                  </span>
                </div>
                <div className="mt-1.5 space-y-1">
                  {rubric.dimensions.map((dimension) => {
                    const current = entry[dimension.id];
                    return (
                      <div key={dimension.id} className="flex items-center justify-between gap-2">
                        <span
                          className="shrink-0 text-[11px] text-slate-400"
                          title={dimension.description}
                        >
                          {dimension.label}
                        </span>
                        <div className="flex gap-0.5">
                          {SCORE_VALUES.map((value) => (
                            <button
                              key={value}
                              type="button"
                              data-testid={`score-btn-${participant.id}-${dimension.id}-${value}`}
                              aria-pressed={current === value}
                              onClick={() =>
                                setCoachScore(
                                  participant.id,
                                  dimension.id,
                                  current === value ? 0 : value,
                                )
                              }
                              className={cx(
                                'h-5 w-5 rounded border text-[10px] transition-colors',
                                current === value
                                  ? 'border-double-blind bg-double-blind text-white'
                                  : 'border-white/10 bg-black/20 text-slate-400 hover:border-white/30',
                              )}
                            >
                              {value}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}
